import { Link, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Home,
  LogOut,
  Package,
  Settings,
  Users,
  Wheat,
} from 'lucide-react';
import { useState } from 'react';
import { authClient } from '@/lib/auth';
import type { Role } from '@/lib/schemas';
import { cn } from '@/lib/utils';

interface SidebarNavProps {
  role: Role;
}

export function SidebarNav({ role }: SidebarNavProps) {
  const [herdOpen, setHerdOpen] = useState(true);
  const [feedOpen, setFeedOpen] = useState(true);
  const location = useRouterState({ select: (s) => s.location.pathname });

  const isFarmer = role === 'farmer';

  function navLink(to: string, label: string, icon: React.ReactNode, exact = false) {
    const active = exact ? location === to : location.startsWith(to);
    return (
      <Link
        to={to}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          active ? 'bg-ndd-800 text-white' : 'text-ndd-400 hover:bg-ndd-900 hover:text-white'
        )}
      >
        {icon}
        <span>{label}</span>
      </Link>
    );
  }

  function subLink(to: string, label: string, search?: string) {
    const fullPath = search ? `${to}?${search}` : to;
    const active = search
      ? location === to && typeof window !== 'undefined' && window.location.search.includes(search)
      : location === to;
    return (
      <Link
        to={fullPath as '/herd'}
        className={cn(
          'flex items-center gap-3 rounded-lg py-1.5 pl-10 pr-3 text-sm transition-colors',
          active ? 'text-white' : 'text-ndd-500 hover:text-ndd-300'
        )}
      >
        {label}
      </Link>
    );
  }

  return (
    <div className="flex h-screen w-[220px] shrink-0 flex-col border-r border-ndd-800 bg-ndd-950">
      {/* Logo */}
      <div className="border-b border-ndd-800 px-4 py-4">
        <h1 className="text-lg font-bold text-white">New Day Dairy</h1>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {navLink('/', 'Home', <Home size={18} />, true)}

        {/* Herd section */}
        <button
          type="button"
          onClick={() => setHerdOpen((v) => !v)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ndd-400 hover:bg-ndd-900 hover:text-white transition-colors"
        >
          <Users size={18} />
          <span className="flex-1 text-left">Herd</span>
          {herdOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {herdOpen && (
          <div className="space-y-0.5">
            {subLink('/herd', 'All')}
            {subLink('/herd', 'Due', 'due_within_days=14')}
            {subLink('/herd', 'Dry', 'status=Dry')}
            {subLink('/herd', 'Open', 'preg_status=Open')}
          </div>
        )}

        {/* Feed section */}
        <button
          type="button"
          onClick={() => setFeedOpen((v) => !v)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ndd-400 hover:bg-ndd-900 hover:text-white transition-colors"
        >
          <Wheat size={18} />
          <span className="flex-1 text-left">Feed</span>
          {feedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {feedOpen && (
          <div className="space-y-0.5">
            {subLink('/log/feeding', 'Log')}
            {subLink('/inventory', 'Inventory')}
            {subLink('/ration', 'Ration')}
          </div>
        )}

        {navLink('/log/event', 'Events', <ClipboardList size={18} />)}
        {navLink('/alerts', 'Alerts', <AlertTriangle size={18} />)}
        {navLink('/views', 'Views', <BookOpen size={18} />)}

        {isFarmer && navLink('/audit', 'Audit', <Package size={18} />)}
        {isFarmer && navLink('/config', 'Config', <Settings size={18} />)}
      </nav>

      {/* Footer */}
      <div className="border-t border-ndd-800 px-2 py-3 space-y-2">
        <button
          type="button"
          onClick={async () => {
            await authClient.signOut();
            window.location.href = '/login';
          }}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-ndd-900 hover:text-red-300 transition-colors"
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
}
